import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { paths } from '@/routes/paths';
import { useOrganization } from '@/context/organization-context';
import { AssetGrid } from '@/components/media-library/AssetGrid';
import { AssetList } from '@/components/media-library/AssetList';
import { CanvasGrid } from '@/components/media-library/CanvasGrid';
import { useNavigate, createFileRoute } from '@tanstack/react-router';
import { ImportUrlModal } from '@/components/media-library/ImportUrlModal';
import { CreateGifModal } from '@/components/media-library/CreateGifModal';
import { AssetUploadModal } from '@/components/media-library/AssetUploadModal';
import { AssetDetailModal } from '@/components/media-library/AssetDetailModal';
import { CollectionsSidebar } from '@/components/media-library/CollectionsSidebar';
import { MediaLibraryToolbar } from '@/components/media-library/MediaLibraryToolbar';
import { AddToCollectionModal } from '@/components/media-library/AddToCollectionModal';
import { useCanvasesControllerCreate } from '@/apis/backend/generated/canvases/canvases';
import {
  useMediaLibraryControllerDeleteAsset,
  useMediaLibraryControllerUploadAsset,
} from '@/apis/backend/generated/media-library/media-library';

import { useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  Drawer,
  Dialog,
  Container,
  Typography,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  DialogContentText,
} from '@mui/material';
import {
  Add as AddIcon,
  Gif as GifIcon,
  Link as LinkIcon,
  Menu as MenuIcon,
  Brush as BrushIcon,
  Delete as DeleteIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  CreateNewFolder as CreateNewFolderIcon,
} from '@mui/icons-material';

type ViewMode = 'grid' | 'list';

interface MediaLibrarySearch {
  view?: string;
}

export const Route = createFileRoute('/_authed/dashboard/o/$orgId/c/$clientId/media-library/')({
  component: MediaLibraryPage,
  validateSearch: (search: Record<string, unknown>): MediaLibrarySearch => ({
    view: typeof search.view === 'string' ? search.view : undefined,
  }),
});

function MediaLibraryPage() {
  const { t } = useTranslation('mediaLibrary');
  const navigate = useNavigate();
  const mlTheme = useTheme();
  const isMobileML = useMediaQuery(mlTheme.breakpoints.down('md'));

  const { orgId, clientId } = Route.useParams();
  const base = paths.dashboard.tenantRoot(orgId, clientId);

  // URL-driven collection filter
  const { view } = Route.useSearch();
  const selectedCollection = view ?? null;

  // State management
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<any>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Selection state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);

  // Filter state
  const [search, setSearch] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedAssetTypes, setSelectedAssetTypes] = useState<string[]>([]);
  const [selectedSources, setSelectedSources] = useState<string[]>([]);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Add to collection modal state
  const [addToCollectionModalOpen, setAddToCollectionModalOpen] = useState(false);
  const [assetsToAddToCollection, setAssetsToAddToCollection] = useState<string[]>([]);

  // Import URL and Create GIF modal state
  const [importUrlModalOpen, setImportUrlModalOpen] = useState(false);
  const [createGifModalOpen, setCreateGifModalOpen] = useState(false);

  // Get organization and client context
  const { activeOrganization, activeClient } = useOrganization();
  const queryClient = useQueryClient();
  const deleteMutation = useMediaLibraryControllerDeleteAsset();
  const uploadAssetMutation = useMediaLibraryControllerUploadAsset();
  const createCanvasMutation = useCanvasesControllerCreate();

  const handleViewAsset = (asset: any) => {
    setSelectedAsset(asset);
    setDetailModalOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailModalOpen(false);
    setSelectedAsset(null);
  };

  const handleToggleViewMode = () => {
    setViewMode((prev) => (prev === 'grid' ? 'list' : 'grid'));
  };

  const handleCollectionSelect = (collectionId: string | null) => {
    navigate({
      to: `${base}/media-library`,
      search: collectionId ? { view: collectionId } : {},
      replace: true,
    });
  };

  const handleToggleSelectionMode = () => {
    setSelectionMode((prev) => !prev);
    if (selectionMode) {
      setSelectedAssetIds([]); // Clear selection when exiting selection mode
    }
  };

  const handleSelectionChange = (assetIds: string[]) => {
    setSelectedAssetIds(assetIds);
    // Auto-enter selection mode when user selects an asset
    if (assetIds.length > 0 && !selectionMode) {
      setSelectionMode(true);
    }
    // Auto-exit selection mode when all assets are deselected
    if (assetIds.length === 0 && selectionMode) {
      setSelectionMode(false);
    }
  };

  const handleBulkDeleteClick = () => {
    if (!activeOrganization?.id || !activeClient?.id) return;
    if (selectedAssetIds.length === 0) return;
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!activeOrganization?.id || !activeClient?.id) return;

    try {
      // Delete each selected asset
      await Promise.all(
        selectedAssetIds.map((assetId) =>
          deleteMutation.mutateAsync({
            orgId: activeOrganization.id,
            clientId: activeClient.id,
            id: assetId,
          })
        )
      );

      // Clear selection and close dialog
      setSelectedAssetIds([]);
      setSelectionMode(false);
      setDeleteDialogOpen(false);

      // Refresh the asset list
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return typeof key === 'string' && key.includes('/media/');
        },
      });
    } catch (error) {
      console.error('Failed to delete assets:', error);
      setDeleteError(t('mediaLibrary.deleteError', 'Failed to delete some assets. Please try again.'));
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeleteError(null);
  };

  const handleAddToCollection = (assetIds: string[]) => {
    setAssetsToAddToCollection(assetIds);
    setAddToCollectionModalOpen(true);
  };

  const handleAddToCollectionClose = () => {
    setAddToCollectionModalOpen(false);
    setAssetsToAddToCollection([]);
  };

  const handleBulkAddToCollection = () => {
    if (selectedAssetIds.length === 0) return;
    handleAddToCollection(selectedAssetIds);
  };

  const handleCreateCanvas = async () => {
    if (!activeOrganization?.id || !activeClient?.id) return;
    try {
      const canvas = await createCanvasMutation.mutateAsync({
        orgId: activeOrganization.id,
        clientId: activeClient.id,
        data: {},
      });
      navigate({ to: `${base}/media-library/canvas/${canvas.id}` });
    } catch (error) {
      console.error('Failed to create canvas:', error);
    }
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* Collections Sidebar */}
      <Drawer
        variant={isMobileML ? 'temporary' : 'persistent'}
        open={isMobileML ? sidebarOpen : true}
        onClose={() => setSidebarOpen(false)}
        sx={{
          width: 280,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 280,
            boxSizing: 'border-box',
            position: isMobileML ? 'fixed' : 'relative',
            borderRight: 1,
            borderColor: 'divider',
          },
        }}
      >
        <CollectionsSidebar
          selectedCollection={selectedCollection}
          onSelectCollection={(id) => {
            handleCollectionSelect(id);
            if (isMobileML) setSidebarOpen(false);
          }}
        />
      </Drawer>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Container
          maxWidth="xl"
          sx={{
            py: { xs: 2, sm: 4 },
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {isMobileML && (
                <IconButton onClick={() => setSidebarOpen(true)} edge="start" size="small">
                  <MenuIcon />
                </IconButton>
              )}
              <Typography
                variant="h3"
                fontWeight="bold"
                sx={{ fontSize: { xs: 24, sm: 'inherit' }, lineHeight: 1.2 }}
              >
                {t('mediaLibrary.title', 'Media')}
              </Typography>
              {selectionMode && selectedAssetIds.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {t('mediaLibrary.selectedCount', '{{count}} selected', { count: selectedAssetIds.length })}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              {/* Bulk action buttons */}
              {selectionMode && selectedAssetIds.length > 0 && (
                <>
                  {selectedAssetIds.length >= 2 && (
                    <IconButton
                      size="small"
                      onClick={() => setCreateGifModalOpen(true)}
                      title={t('mediaLibrary.createGifFromImages', 'Create GIF from {{count}} images', { count: selectedAssetIds.length })}
                    >
                      <GifIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    onClick={handleBulkAddToCollection}
                    title={t('mediaLibrary.addToCollection', 'Add to Collection')}
                  >
                    <CreateNewFolderIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={handleBulkDeleteClick}
                    title={t('mediaLibrary.deleteCount', 'Delete ({{count}})', { count: selectedAssetIds.length })}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              )}

              <IconButton
                size="small"
                onClick={handleToggleViewMode}
                color={viewMode === 'grid' ? 'primary' : 'default'}
              >
                <ViewModuleIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleToggleViewMode}
                color={viewMode === 'list' ? 'primary' : 'default'}
              >
                <ViewListIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setImportUrlModalOpen(true)}
                title={t('mediaLibrary.importFromUrl', 'Import from URL')}
              >
                <LinkIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setCreateGifModalOpen(true)}
                title={t('mediaLibrary.createGif', 'Create GIF')}
              >
                <GifIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleCreateCanvas}
                disabled={createCanvasMutation.isPending}
                title={t('mediaLibrary.createWithCanvas', 'Create with Canvas')}
              >
                <BrushIcon fontSize="small" />
              </IconButton>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setUploadModalOpen(true)}
              >
                {t('mediaLibrary.upload', 'Upload')}
              </Button>
            </Box>
          </Box>

          {/* Toolbar with search and filters */}
          <Box sx={{ mb: 2 }}>
            <MediaLibraryToolbar
              search={search}
              onSearchChange={setSearch}
              selectedTags={selectedTags}
              onTagsChange={setSelectedTags}
              selectedCategories={selectedCategories}
              onCategoriesChange={setSelectedCategories}
              selectedAssetTypes={selectedAssetTypes}
              onAssetTypesChange={setSelectedAssetTypes}
              selectedSources={selectedSources}
              onSourcesChange={setSelectedSources}
            />
          </Box>

          {/* Asset Display */}
          <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
            {selectedCollection === 'canvases' ? (
              <CanvasGrid search={search} />
            ) : viewMode === 'grid' ? (
              <AssetGrid
                search={search}
                selectedTags={selectedTags}
                selectedCategories={selectedCategories}
                selectedAssetTypes={selectedAssetTypes}
                selectedSources={selectedSources}
                selectedCollection={selectedCollection}
                onViewAsset={handleViewAsset}
                onAddToCollection={handleAddToCollection}
                selectionMode={selectionMode}
                selectedAssets={selectedAssetIds}
                onSelectionChange={handleSelectionChange}
              />
            ) : (
              <AssetList
                search={search}
                selectedTags={selectedTags}
                selectedCategories={selectedCategories}
                selectedAssetTypes={selectedAssetTypes}
                selectedSources={selectedSources}
                selectedCollection={selectedCollection}
                onViewAsset={handleViewAsset}
                selectionMode={selectionMode}
                selectedAssets={selectedAssetIds}
                onSelectionChange={handleSelectionChange}
              />
            )}
          </Box>
        </Container>
      </Box>

      {/* Upload Modal */}
      <AssetUploadModal open={uploadModalOpen} onClose={() => setUploadModalOpen(false)} />

      {/* Import URL Modal */}
      <ImportUrlModal open={importUrlModalOpen} onClose={() => setImportUrlModalOpen(false)} />

      {/* Create GIF Modal */}
      <CreateGifModal
        open={createGifModalOpen}
        onClose={() => setCreateGifModalOpen(false)}
        preSelectedAssetIds={selectionMode ? selectedAssetIds : []}
      />

      {/* Asset Detail Modal */}
      {selectedAsset && (
        <AssetDetailModal open={detailModalOpen} asset={selectedAsset} onClose={handleCloseDetail} />
      )}

      {/* Add to Collection Modal */}
      <AddToCollectionModal
        open={addToCollectionModalOpen}
        onClose={handleAddToCollectionClose}
        assetIds={assetsToAddToCollection}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">{t('mediaLibrary.deleteDialog.title', 'Delete Assets')}</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {t('mediaLibrary.deleteDialog.body', 'Are you sure you want to delete {{count}} asset(s)? This action cannot be undone.', { count: selectedAssetIds.length })}
          </DialogContentText>
          {deleteError && (
            <Typography color="error" sx={{ mt: 2 }}>
              {deleteError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteMutation.isPending}>
            {t('common:cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? t('mediaLibrary.deleting', 'Deleting...') : t('common:delete')}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
